/**
 * Payment-method display helpers
 *
 * One place that turns a card OR bank (ACH) payment method into display strings, so
 * every surface (selectors, lists, receipts) renders a migrated bank method as
 * "Wells Fargo •••• 6789" instead of a blank/garbage card. Accepts a tolerant
 * structural shape so it works with the app-local `PaymentMethod` types and the
 * shared `PaymentMethodDisplay` from `@lgc/database` without mapping.
 */

const MASK = '••••'

/** Tolerant structural shape covering both card and bank fields (all optional). */
export interface PaymentMethodLike {
  methodType?: 'card' | 'bank' | string | null
  // Card
  cardBrand?: string | null
  last4?: string | null
  expMonth?: number | null
  expYear?: number | null
  // Bank (ACH)
  bankName?: string | null
  bankLast4?: string | null
  bankAccountType?: string | null // 'checking' | 'savings'
  accountHolderType?: string | null // 'individual' | 'company'
}

export interface PaymentMethodDescription {
  isBank: boolean
  /** Brand (card) or bank name; capitalized brand, verbatim bank name. Falls back to 'Card'/'Bank'. */
  name: string
  /** Last 4 of the card or bank account; null if unknown. */
  last4: string | null
  /** Full label, e.g. 'Visa •••• 4242' or 'Wells Fargo •••• 6789'. */
  label: string
  /** Secondary line: 'Expires 12/27' (card) or 'Checking' (bank); null if none. */
  sublabel: string | null
}

export function isBankPaymentMethod(pm: PaymentMethodLike): boolean {
  return pm.methodType === 'bank'
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s
}

export function describePaymentMethod(pm: PaymentMethodLike): PaymentMethodDescription {
  if (isBankPaymentMethod(pm)) {
    const name = pm.bankName || 'Bank'
    // Prefer bankLast4, but fall back to a flattened `last4` (the shape API list-items use,
    // e.g. UserPaymentMethodListItem) so those consumers work without a manual remap.
    const last4 = pm.bankLast4 ?? pm.last4 ?? null
    const sublabel = pm.bankAccountType ? capitalize(pm.bankAccountType) : null
    return { isBank: true, name, last4, label: `${name} ${MASK} ${last4 ?? MASK}`, sublabel }
  }

  const name = pm.cardBrand ? capitalize(pm.cardBrand) : 'Card'
  const last4 = pm.last4 ?? null
  const sublabel =
    pm.expMonth && pm.expYear
      ? `Expires ${String(pm.expMonth).padStart(2, '0')}/${String(pm.expYear).slice(-2)}`
      : null
  return { isBank: false, name, last4, label: `${name} ${MASK} ${last4 ?? MASK}`, sublabel }
}
